import axiosInstance from "../../api";
import { TreeSelect, Modal } from "antd";
import type { TreeSelectProps } from "antd";
import { useCallback, useEffect, useState } from "react";
import { any, object, TypeOf, z } from "zod";
import { Box, Button, TextField, useTheme } from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import type { DefaultOptionType } from "antd/es/select";
import { useDispatch, useSelector } from "react-redux";
import { useForm, SubmitHandler } from "react-hook-form";
import {
  loadCompanyList,
  saveCompany,
  updateCompany,
} from "../../redux/actions/companyward.actions";
import { storeStateType } from "../../redux/store";

interface Props {
  isDialogOpened: boolean;
  handleCloseDialog: any;
  action: string;
  addSubFacilty: boolean;
  filterData: any;
}

const formSchema = object({
  id: any(),
  parent: z.any(),
  facility_name: z.string().min(1, { message: "Company name is required" }),
  facility_status: any(),
});
type FormInput = TypeOf<typeof formSchema>;

const defaultValues = {
  parent: "",
  facility_name: "",
  faiclity_status: "",
};

export function WardsForm(props: Props) {
  const { handleCloseDialog } = props;

  const theme = useTheme();

  const dispatch = useDispatch();
  const { themeMode } = useSelector((state: storeStateType) => state.UI);
  const { companylist, ward, save } = useSelector(
    (state: storeStateType) => state.companyward
  );
  const {
    register,
    reset,
    getValues,
    formState: { errors },
    handleSubmit,
  } = useForm<FormInput>({ resolver: zodResolver(formSchema) });
  const [treeData, setTreeData] = useState<Omit<DefaultOptionType, "label">[]>(
    []
  );
  const [parent, setParent] = useState<string>();
  const [expandedKeys, setExpandedKeys] = useState<any[]>([]);
  const [treeLoadedKeys, setTreeLoadedKeys] = useState<any[]>([]);

  useEffect(() => {
    if (props.addSubFacilty) {
      setExpandedKeys([]);
      setTreeLoadedKeys([]);
      setParent(undefined);
    }
  }, [props.addSubFacilty]);

  const handleClose = useCallback(() => {
    handleCloseDialog(false);
    reset(defaultValues);
  }, [handleCloseDialog, reset]);

  const resetTreeSelect = () => {
    setExpandedKeys([]);
    setTreeLoadedKeys([]);
    setParent(undefined);
  };

  useEffect(() => {
    if (save) {
      handleClose();
    }
  }, [save, handleClose]);

  useEffect(() => {
    if (ward) {
      reset(ward);
      //setWardId(ward?.parent)
      setParent(ward?.parent);

      const expandIds: any = [];
      let rootItems: any = [];

      if (ward?.parent_nodes) {
        companylist?.forEach((item: any) =>
          rootItems.push({
            id: item.id,
            pId: item.parend_id,
            value: item.id,
            title: item.facility_name,
            isLeaf: !item.child,
            key: item.id,
          })
        );

        ward.parent_nodes.forEach((item: any) => {
          if (item.id !== ward.parent) {
            expandIds.push(item.id);
          }
          if (item.parent) {
            rootItems.push({
              id: item.id,
              pId: item.parent,
              value: item.id,
              title: item.facility_name,
              isLeaf: false,
              key: item.id,
            });
          }
        });
        setTreeData(rootItems);
        setExpandedKeys(expandIds);
        setTreeLoadedKeys(expandIds);
      }
    }
  }, [ward, companylist, reset]);

  useEffect(() => {
    dispatch<any>(loadCompanyList());
  }, [dispatch]);

  useEffect(() => {
    if (companylist) {
      setParent(undefined);
      let rootItems: any = [];
      companylist.forEach((item: any) =>
        rootItems.push({
          id: item.id,
          pId: item.parend_id,
          value: item.id,
          title: item.facility_name,
          isLeaf: !item.child,
          key: item.id,
        })
      );
      setTreeData(rootItems);
    }
  }, [companylist]);

  const onFormSubmit: SubmitHandler<FormInput> = (data) => {
    const paentIdToString: string = data.parent as unknown as string;
    const formData = new FormData();
    formData.append("facility_name", data.facility_name);
    formData.append("parent", paentIdToString);
    if (data.id) {
      formData.append("facility_status", data.facility_status);
      dispatch<any>(updateCompany(formData, data.id, props.filterData));
    } else {
      formData.append("facility_status", "1");
      dispatch<any>(saveCompany(formData, props.filterData));
    }
  };

  const genChildTreeNode = (parentId: number, item: any) => {
    return {
      id: item.id,
      pId: parentId,
      value: item.id,
      title: item.facility_name,
      isLeaf: !item.child,
      key: item.id,
    };
  };
  const onLoadData: TreeSelectProps["loadData"] = ({ id }) =>
    axiosInstance
      .get("subfacility/subfacilitylist/" + id)
      .then((response) => {
        let childIitems: any = [];
        if (!treeLoadedKeys.includes(id)) {
          response.data.forEach((item: any) => {
            childIitems.push(Object.assign({}, genChildTreeNode(id, item)));
          });
          treeLoadedKeys.push(id);
        }
        if (childIitems.length > 0) setTreeData(treeData.concat(childIitems));
      })
      .catch((error) => {
        //console.log(error)
      });

  const onChange = (newValue: string) => {
    setParent(newValue);
    reset({ ...getValues(), parent: newValue });
  };

  return (
    <Modal
      open={props.isDialogOpened}
      onCancel={handleClose}
      onOk={handleClose}
      afterClose={resetTreeSelect}
      closable={false}
      className={themeMode === "dark" ? "antd-dark-theme" : ""}
      style={{ backgroundColor: theme?.palette?.background?.paper }}
      footer={[
        <Button onClick={handleClose}>Close</Button>,
        <Button type="submit" form="myform" color="success">
          Submit
        </Button>,
      ]}
    >
      <Box
        component="form"
        id="myform"
        noValidate
        sx={{ backgroundColor: theme?.palette?.background?.paper }}
        onSubmit={handleSubmit(onFormSubmit)}
      >
        {props.isDialogOpened && (
          <TreeSelect
            key="Treeselect"
            treeDataSimpleMode
            style={{ width: "100%" }}
            value={parent as unknown as string}
            dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
            placeholder="Please select Facilty/Ward"
            onChange={onChange}
            loadData={onLoadData}
            treeData={treeData}
            allowClear={true}
            status={errors["parent"] ? "error" : ""}
            {...(expandedKeys.length > 0 && {
              treeDefaultExpandedKeys: expandedKeys,
            })}
            {...(treeLoadedKeys.length > 0 && {
              treeLoadedKeys: treeLoadedKeys,
            })}
          />
        )}
        <TextField
          id="parent_id"
          type="hidden"
          sx={{ display: "none" }}
          {...register("parent")}
        />
        <TextField
          autoFocus
          margin="dense"
          id="name"
          label="Ward name"
          fullWidth
          variant="standard"
          required
          {...register("facility_name")}
          error={!!errors["facility_name"]}
          helperText={
            errors["facility_name"] ? errors["facility_name"].message : ""
          }
        />
      </Box>
    </Modal>
  );
}
